import React from "react";
import { Sparkles } from "lucide-react";

/**
 * MobiHint — a floating inline "Ask Mobi" chip.
 *
 * Dropped on any content page (Quiz Maker, Game Creator, Screenshot Quiz, etc.) to
 * make Mobi more discoverable. Clicking dispatches an `open-mobi` window event
 * (with an optional pre-seeded prompt) which the top-header MobiAvatar listens to.
 *
 * Props:
 *   prompt   — optional prompt to pre-fill into the Mobi input
 *   label    — button text (defaults to "Ask Mobi")
 *   position — "float" (default, bottom-right floater) | "inline" (inline pill)
 */
export default function MobiHint({ prompt, label = "Ask Mobi", position = "float", bottom = "bottom-5" }) {
  const fire = () => window.dispatchEvent(new CustomEvent("open-mobi", { detail: { prompt } }));

  if (position === "inline") {
    return (
      <button
        onClick={fire}
        data-testid="mobi-hint-inline"
        className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-2.5 py-1.5 rounded-full bg-gradient-to-r from-[#FFD600]/15 to-[#7C3AED]/15 border border-[#FFB400]/40 hover:border-[#FFB400] text-[#0A0C10] hover:scale-[1.02] transition-transform"
      >
        <Sparkles size={11} className="text-[#FFB400]" /> {label}
      </button>
    );
  }

  return (
    <button
      onClick={fire}
      data-testid="mobi-hint-float"
      title="Ask Mobi about this page"
      className={`fixed ${bottom} right-5 z-40 inline-flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-[#0A0C10] text-white shadow-[0_18px_36px_-12px_rgba(124,58,237,0.5),0_0_0_1px_rgba(255,180,0,0.25)] hover:shadow-[0_22px_44px_-14px_rgba(124,58,237,0.65),0_0_0_1px_rgba(255,180,0,0.5)] hover:-translate-y-0.5 transition-all group`}
    >
      <span className="relative w-6 h-6 rounded-full overflow-hidden">
        <span className="absolute inset-0 rounded-full bg-gradient-to-br from-[#FFD600] via-[#FF8A00] to-[#7C3AED] animate-pulse" />
        <span className="absolute inset-[2px] rounded-full bg-[#0A0C10] flex items-center justify-center text-[#FFB400] font-bold text-[10px]">M</span>
      </span>
      <span className="text-sm font-semibold">{label}</span>
      <Sparkles size={12} className="text-[#FFD600] opacity-70 group-hover:opacity-100" />
    </button>
  );
}
